import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { selectUser } from '@/store/selectors/userSelectors';
import PromptMessage from '@/components/UI/PromptMessage';
import Spinner from '@/components/UI/Spinner';
import { AuthMethod, BffAuthenticationMethodResponse } from '@lucky7ventures/bff-types';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import BffApiService from '@/shared/bffApiService';
import styled from 'styled-components';

export const StyledSwitch = styled.label`
  display: flex !important;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  font-weight: bold;

  > span {
    flex: 1;
    margin-right: 1rem;
  }

  .switch {
    position: relative;
    display: inline-block;
    width: 48px;
    height: 24px;

    input {
      opacity: 0;
      width: 0;
      height: 0;
    }
  }

  /* The slider */

  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${props => props.theme.colors.grey20};
    -webkit-transition: 0.2s;
    transition: 0.2s;
    border-radius: 24px;
  }

  .slider:before {
    position: absolute;
    content: '';
    height: 1rem;
    width: 1rem;
    left: 4px;
    bottom: 4px;
    background-color: white;
    -webkit-transition: 0.2s;
    transition: 0.2s;
    border-radius: 50%;
  }

  input:checked + .slider {
    background-color: ${props => props.theme.colors.green40};
  }

  input:checked + .slider:before {
    transform: translateX(24px);
  }
`;

const Settings2FA = (): JSX.Element => {
  const intl = useIntl();
  const user = useSelector(selectUser);
  const [authenticationMethod, setAuthenticationMethod] = useState<AuthMethod>(
    AuthMethod.OneFactor,
  );
  const { request: getAuthenticationMethodRequest } =
    useBffApiRequest<BffAuthenticationMethodResponse>();
  const {
    request: setAuthenticationMethodRequest,
    loading: setAuthLoading,
    success: setAuthSuccess,
  } = useBffApiRequest<null>();

  useEffect(() => {
    const handleOnSuccess = (method: BffAuthenticationMethodResponse) => {
      setAuthenticationMethod(method);
    };

    getAuthenticationMethodRequest({
      apiMethod: BffApiService.getAuthenticationMethod,
      successCallback: handleOnSuccess,
    });
  }, []);

  const submit = () => {
    setAuthenticationMethodRequest({
      apiMethod: BffApiService.setAuthenticationMethod,
      pathParams: {
        authMethod: authenticationMethod,
      },
    });
  };

  const toggleTwoFactor = () => {
    setAuthenticationMethod(prevState =>
      prevState === AuthMethod.OneFactor ? AuthMethod.TwoFactorBySms : AuthMethod.OneFactor,
    );
  };

  return (
    <section className="rounded bg-white p-8">
      <h3 className="text-2xl font-bold">{intl.formatMessage({ id: 'settings.otp.title' })}</h3>
      <p className="my-6">{intl.formatMessage({ id: 'settings.otp.description' })}</p>
      <div className="mb-8 w-full">
        <div className="text-sm opacity-50">
          {intl.formatMessage({ id: 'settings.otp.registeredPhoneLabel' })}{' '}
          {`${user?.mobilePrefix} ${user?.mobile}`}
        </div>
        <StyledSwitch htmlFor="twoFactorSms">
          {intl.formatMessage({ id: 'inputs.toggle.sms' })}
          <div className="switch">
            <input
              id="twoFactorSms"
              type="checkbox"
              checked={authenticationMethod === AuthMethod.TwoFactorBySms}
              onChange={toggleTwoFactor}
            />
            <span className="slider" />
          </div>
        </StyledSwitch>
      </div>
      {setAuthSuccess && (
        <div className="w-full">
          <PromptMessage
            type="success"
            message={intl.formatMessage({
              id: 'settings.details.successMessage',
            })}
          />
        </div>
      )}
      <div className="flex w-full justify-end">
        <button
          type="button"
          className="rounded-full bg-blue-light px-10 py-4 font-bold text-white shadow-md transition hover:bg-cyan-600 hover:shadow-lg md:w-auto"
          onClick={submit}
        >
          {setAuthLoading ? (
            <Spinner color="white" height={22} width={22} borderwidth={3} />
          ) : (
            intl.formatMessage({ id: 'settings.details.saveButton' })
          )}
        </button>
      </div>
    </section>
  );
};

export default Settings2FA;
